import {FC, useRef, useState} from 'react';
import Icon from 'components/Icon';
import {ButtonNew} from 'components/ButtonNew';
import classNames from 'classnames';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {showAuthDialog} from 'modules/auth/thunks';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';
import {Typography} from 'components/Typography';
import {Tooltip} from 'components/Tooltip';
import Link from 'components/CustomRoutingLink';
import style from './style.module.scss';
import {useClickOutside} from 'core/hooks/useClickOutside';
import {useAddProductToWishlistMutation} from 'modules/wishlist_new/services';
import {TAddToWishListResponse} from 'modules/wishlist_new/models';
import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query/react';
import {SerializedError} from '@reduxjs/toolkit';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import {useWishlistEvents} from 'hooks/analytics/gtag/wishlist/useWishlistEventsGtag';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';

interface IWishListButtonProps {
    id: IProduct['id'];
    className?: string;
}

export const WishlistButton: FC<IWishListButtonProps> = ({className, id}) => {
    const dispatch = useAppDispatch();
    const productData = useAppSelector(selectProductData);
    const {sendAddToWishlistEvent} = useWishlistEvents();

    const isMobile = useIsMobileByScreen();

    const overlayRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const isLoggedIn = useIsLoggedIn();

    const [addToWishList, {isLoading}] = useAddProductToWishlistMutation();

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [tooltipContent, setTooltipContent] = useState<{
        title: string;
        message: string;
    } | null>(null);

    useClickOutside([overlayRef, wrapperRef], () => {
        if (isTooltipOpen) {
            setIsTooltipOpen(false);
        }
    });

    const extractErrorCode = (response: FetchBaseQueryError | SerializedError | undefined) => {
        if (!response || 'object' !== typeof response) {
            return undefined;
        }

        const errorData = 'data' in response ? (response as Record<string, unknown>).data : response;

        if (errorData && 'object' === typeof errorData && 'errors' in errorData) {
            return (errorData as TAddToWishListResponse)?.errors?.code;
        }

        return undefined;
    };

    const openTooltip = (content: {title: string; message: string}) => {
        setTooltipContent(content);
        setIsTooltipOpen(true);
    };

    const handleErrorCode = (errorCode?: string) => {
        if (!errorCode) {
            return false;
        }

        if ('UNAUTHORIZED' === errorCode) {
            dispatch(showAuthDialog);
            return true;
        }

        if ('LIMIT_REACHED' === errorCode) {
            openTooltip({
                title: 'Список заполнен',
                message: 'Освободите место для новых желаний',
            });
            return true;
        }

        return false;
    };

    const handleSuccessStatus = () => {
        openTooltip({title: 'Товар добавлен', message: ''});
    };

    const handleAddToWishlist = async () => {
        if (!isLoggedIn) {
            dispatch(showAuthDialog);
            return undefined;
        }

        setIsTooltipOpen(false);

        try {
            const response = await addToWishList({article: id});
            if ('error' in response) {
                return handleErrorCode(extractErrorCode(response.error));
            }
            if (response?.data?.data) {
                sendAddToWishlistEvent(productData, response.data.data);
            }

            handleSuccessStatus();
        } catch (error) {}

        return undefined;
    };

    const handleCloseTooltip = () => {
        setIsTooltipOpen(false);
    };

    return (
        <div className={classNames('add-to-wishlist', style.root)}>
            <Tooltip
                overlayRef={overlayRef}
                className={style.tooltip}
                overlay={
                    <div className={style.tooltipContent}>
                        <div className={style.close} onClick={handleCloseTooltip}>
                            <Icon icon={'close'} width={20} height={20} />
                        </div>
                        <Typography variant={'label-14-m'}>{tooltipContent?.title}</Typography>
                        {tooltipContent?.message && (
                            <Typography variant={'label-12-r'}>{tooltipContent.message}</Typography>
                        )}
                        <Link href={'/personal/wishlist'} className={style.link}>
                            <Typography variant={'label-12-r'}>Перейти в список желаний</Typography>
                        </Link>
                    </div>
                }
                isOpen={Boolean(isTooltipOpen && tooltipContent)}
                positionStrategy={'fixed'}
                place={isMobile ? 'bottom-end' : 'bottom'}
                delayShow={300}
                delayHide={300}
            >
                <ButtonNew
                    className={classNames(className)}
                    onClick={handleAddToWishlist}
                    theme={'gray'}
                    variant={'small'}
                    leftIcon={<Icon icon={'wishlist'} />}
                    isLoading={isLoading}
                    isStretched={true}
                >
                    В список желаний
                </ButtonNew>
            </Tooltip>
        </div>
    );
};
